import { FC, useCallback } from 'react'
import { NonUndefined } from 'react-hook-form'

import { ActionTimer, idleTimer } from '@/hooks/useActionTimer'
import useObject from '@/hooks/useObject'
import { SSO, SSOProvider } from '@/out/single-sign-on'
import { retrieveSSO } from '@/thunks/single-sign-on/retrieve'
import { SSOCredentials } from '@/thunks/single-sign-on/upsert'

import AzureAD from './azure'
import OIDC from './oidc'
import Okta from './okta'
import useSSORemove from './useSSORemove'
import useSSOUpsert from './useSSOUpsert'
import { Settings } from '@/components/layout/settings/layout'
import SettingsTitle from '@/components/layout/settings/title'

export type SSOProps<T extends SSOProvider> = {
  setupTimer: ActionTimer
  onSetup: (provider: T, config: NonUndefined<SSOCredentials[T]>) => Promise<SSO>
  removeTimer: ActionTimer
  onRemove: (provider: SSOProvider) => void
}

export type FormProps<T extends SSOProvider> = {
  timer: ActionTimer
  onSetup: (config: NonUndefined<SSOCredentials[T]>) => void
}

const SingleSignOn: FC = () => {
  const [configuration, { patch, wasLoaded }] = useObject(retrieveSSO, {})

  const [upsert, { timer: setupTimer }] = useSSOUpsert((provider, config) => patch({ [provider]: config }))
  const [remove, { timer: removeTimer }] = useSSORemove(provider => patch({ [provider]: undefined }))

  const props = useCallback(
    <T extends SSOProvider>(provider: T): SSOProps<T> => ({
      onSetup: upsert,
      setupTimer: setupTimer.id === provider ? setupTimer : idleTimer,
      onRemove: remove,
      removeTimer: removeTimer.id === provider ? removeTimer : idleTimer,
    }),
    [upsert, remove, setupTimer, removeTimer]
  )

  // Find active providers
  const activeProvider =
    configuration &&
    Object.entries(configuration).find(
      ([_, config]) => config && typeof config === 'object' && Object.keys(config).length > 0
    )

  // Create sorted providers array with connected provider first
  const sortedProviders = () => {
    const providers = [
      {
        key: 'azure',
        component: (
          <AzureAD
            key="azure"
            configuration={configuration?.azure}
            disabled={activeProvider && activeProvider[0] !== 'azure'}
            {...props('azure')}
          />
        ),
        isConnected: !!(configuration?.azure && Object.keys(configuration.azure).length > 0),
      },
      {
        key: 'okta',
        component: (
          <Okta
            key="okta"
            configuration={configuration?.okta}
            disabled={activeProvider && activeProvider[0] !== 'okta'}
            {...props('okta')}
          />
        ),
        isConnected: !!(configuration?.okta && Object.keys(configuration.okta).length > 0),
      },
      {
        key: 'oidc',
        component: (
          <OIDC
            key="oidc"
            configuration={configuration?.oidc}
            disabled={activeProvider && activeProvider[0] !== 'oidc'}
            {...props('oidc')}
          />
        ),
        isConnected: !!(configuration?.oidc && Object.keys(configuration.oidc).length > 0),
      },
    ]

    // Sort so connected provider appears first
    return providers.sort((a, b) => {
      if (a.isConnected && !b.isConnected) return -1
      if (!a.isConnected && b.isConnected) return 1
      return 0
    })
  }

  return (
    <Settings>
      <SettingsTitle
        title="Single Sign-On"
        subtitle="Configure external data store providers by linking your account to third-party services."
      />

      {wasLoaded && configuration && (
        <div className="space-y-4">{sortedProviders().map(provider => provider.component)}</div>
      )}
    </Settings>
  )
}

export default SingleSignOn
